if (!customElements.get('wlsn-main-cart')) {
  class WLSNMainCart extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        loading: 'loading',
        mainCart: 'wlsn-main-cart',
        loadingState: 'wlsn-loading-state',
        wrapperMainCart: 'js-wlsn-main-cart-wrapper',
      };

      this.isLoading = false;
      this.currentCart = null;
      this.sectionId = this.dataset.sectionId;

      this.bindMethods = {
        handleQuantityChange: this._handleQuantityChange.bind(this),
        handleSubscriptionChange: this._handleSubscriptionChange.bind(this),
        handleItemRemove: this._handleItemRemove.bind(this),
        handleNoteChange: this._handleNoteChange.bind(this),
      };

      this.routes = {
        cartChangeUrl: window.routes.cart_change_url,
        cartUpdateUrl: window.routes.cart_update_url,
        cartUrl: window.routes.cart_url,
      };

      this.sectionsToUpdate = [
        {
          id: 'wlsn-cart-icon-buble',
          selector: '#cart-icon-bubble',
          // updateMethod is optional - if not provided, _updateSectionGeneric will be used
        },
      ];
    }

    /**
     * Get the elements of the cart
     * @returns {object} - The elements of the cart
     */
    get elements() {
      return {
        wrapper: this.querySelector(`.${this.classes.wrapperMainCart}`),
      };
    }

    /**
     * Connect the event listeners
     * @returns {void}
     */
    connectedCallback() {
      this._attachEventListeners();
    }

    /**
     * Disconnect the event listeners
     * @returns {void}
     */
    disconnectedCallback() {
      this._detachEventListeners();
    }

    /**
     * Attach the event listeners
     * @returns {void}
     */
    _attachEventListeners() {
      // Listen for events from child components
      this.addEventListener('wlsn:cart:quantity:change', this.bindMethods.handleQuantityChange);
      this.addEventListener('wlsn:cart:subscription:change', this.bindMethods.handleSubscriptionChange);
      this.addEventListener('wlsn:cart:item:remove', this.bindMethods.handleItemRemove);
      this.addEventListener('wlsn:cart:note:change', this.bindMethods.handleNoteChange);
    }

    /**
     * Detach the event listeners
     * @returns {void}
     */
    _detachEventListeners() {
      this.removeEventListener('wlsn:cart:quantity:change', this.bindMethods.handleQuantityChange);
      this.removeEventListener('wlsn:cart:subscription:change', this.bindMethods.handleSubscriptionChange);
      this.removeEventListener('wlsn:cart:item:remove', this.bindMethods.handleItemRemove);
      this.removeEventListener('wlsn:cart:note:change', this.bindMethods.handleNoteChange);
    }

    /**
     * Handle the quantity change
     * @param {Event} event - The event object
     * @returns {void}
     */
    async _handleQuantityChange(event) {
      const { lineKey, quantity } = event.detail;

      if (this.isLoading) {
        console.warn('Cart update already in progress');
        return;
      }

      try {
        this._broadcastUpdateStart(lineKey, 'quantity');

        const cart = await this._updateCartQuantity(lineKey, quantity);

        this.currentCart = cart;
        this._updateSections(cart);
        await this._reRenderCart();
        this._broadcastUpdateSuccess(lineKey, cart, 'quantity');
      } catch (error) {
        console.error('Failed to update quantity:', error);
        this._broadcastUpdateError(lineKey, error.message);
      }
    }

    /**
     * Handle the subscription change
     * @param {Event} event - The event object
     * @returns {void}
     */
    async _handleSubscriptionChange(event) {
      const { lineKey, lineIndex, sellingPlanId, quantity } = event.detail;

      if (!lineIndex) {
        console.error('❌ CRITICAL: lineIndex is missing in event detail!');
        return;
      }

      if (this.isLoading) {
        console.warn('Cart update already in progress');
        return;
      }

      try {
        this._broadcastUpdateStart(lineKey, 'subscription');

        const cart = await this._updateCartSubscription(lineIndex, sellingPlanId, quantity);

        this.currentCart = cart;
        this._updateSections(cart);
        await this._reRenderCart();
        this._broadcastUpdateSuccess(lineKey, cart, 'subscription');
      } catch (error) {
        console.error('Failed to update subscription:', error);
        this._broadcastUpdateError(lineKey, error.message);
      }
    }

    /**
     * Handle the item remove
     * @param {Event} event - The event object
     * @returns {void}
     */
    async _handleItemRemove(event) {
      const { lineKey } = event.detail;

      if (this.isLoading) {
        console.warn('Cart update already in progress');
        return;
      }

      try {
        this._broadcastUpdateStart(lineKey, 'remove');

        const cart = await this._removeCartItem(lineKey);

        this.currentCart = cart;
        this._updateSections(cart);
        await this._reRenderCart();
        this._broadcastUpdateSuccess(lineKey, cart, 'remove');
      } catch (error) {
        console.error('Failed to remove item:', error);
        this._broadcastUpdateError(lineKey, error.message);
      }
    }

    /**
     * Handle the note change
     * @param {Event} event - The event object
     * @returns {void}
     */
    async _handleNoteChange(event) {
      const { note } = event.detail;

      if (this.isLoading) {
        console.warn('Cart update already in progress');
        return;
      }

      try {
        const cart = await this._updateCartNote(note);
        this.currentCart = cart;

        // Broadcast success without re-rendering (no need to re-render for note change)
        const noteEvent = new CustomEvent('wlsn:cart:note:update:success', {
          detail: { note, cart },
        });
        document.dispatchEvent(noteEvent);
      } catch (error) {
        console.error('Failed to update cart note:', error);

        const errorEvent = new CustomEvent('wlsn:cart:note:update:error', {
          detail: { error: error.message },
        });
        document.dispatchEvent(errorEvent);
      }
    }

    /**
     * Update the cart quantity
     * @param {string} lineKey - The line key of the item that was updated
     * @param {number} quantity - The new quantity
     * @returns {Promise<object>} - The cart object
     */
    async _updateCartQuantity(lineKey, quantity) {
      this._setLoadingState(true);

      try {
        const response = await fetch(`${this.routes.cartChangeUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity,
            sections: this.sectionsToUpdate.map((s) => s.id),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          // Try to get error details from response
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.description || 'Failed to update cart quantity';
          throw new Error(errorMessage);
        }

        const cart = await response.json();
        return cart;
      } finally {
        this._setLoadingState(false);
      }
    }

    /**
     * Update the cart subscription
     * @param {number} lineIndex - The line index of the item that was updated
     * @param {string} sellingPlanId - The selling plan ID
     * @param {number} quantity - The new quantity
     * @returns {Promise<object>} - The cart object
     */
    async _updateCartSubscription(lineIndex, sellingPlanId, quantity) {
      this._setLoadingState(true);

      try {
        // Use 'line' parameter instead of 'id' for subscription changes
        const subscriptionPayload = {
          line: lineIndex,
          selling_plan: sellingPlanId ? parseInt(sellingPlanId) : null,
          sections: this.sectionsToUpdate.map((s) => s.id),
          sections_url: window.location.pathname,
        };

        const subscriptionResponse = await fetch(`${this.routes.cartChangeUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subscriptionPayload),
        });

        if (!subscriptionResponse.ok) {
          const errorText = await subscriptionResponse.text();
          console.error('❌ Shopify API error (subscription):', errorText);
          throw new Error(`Failed to update subscription: ${subscriptionResponse.status}`);
        }

        let cart = await subscriptionResponse.json();

        // Then, if quantity is different than 1, update it
        if (quantity && quantity !== 1) {
          const quantityPayload = {
            line: lineIndex,
            quantity: quantity,
            sections: this.sectionsToUpdate.map((s) => s.id),
            sections_url: window.location.pathname,
          };

          const quantityResponse = await fetch(`${this.routes.cartChangeUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(quantityPayload),
          });

          if (!quantityResponse.ok) {
            console.warn('⚠️ Failed to preserve quantity, continuing anyway');
          } else {
            cart = await quantityResponse.json();
          }
        }

        return cart;
      } finally {
        this._setLoadingState(false);
      }
    }

    /**
     * Remove the cart item
     * @param {string} lineKey - The line key of the item that was removed
     * @returns {Promise<object>} - The cart object
     */
    async _removeCartItem(lineKey) {
      this._setLoadingState(true);

      try {
        const response = await fetch(`${this.routes.cartChangeUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: this.sectionsToUpdate.map((s) => s.id),
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          // Try to get error details from response
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.description || 'Failed to remove item';
          throw new Error(errorMessage);
        }

        const cart = await response.json();
        return cart;
      } finally {
        this._setLoadingState(false);
      }
    }

    /**
     * Update the cart note
     * @param {string} note - The new note
     * @returns {Promise<object>} - The cart object
     */
    async _updateCartNote(note) {
      this._setLoadingState(true);

      try {
        const response = await fetch(`${this.routes.cartUpdateUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            note: note,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.description || 'Failed to update cart note';
          throw new Error(errorMessage);
        }

        const cart = await response.json();
        return cart;
      } finally {
        this._setLoadingState(false);
      }
    }

    /**
     * Re-render the cart
     * @returns {Promise<void>} - Resolves when cart is re-rendered
     */
    async _reRenderCart() {
      try {
        // Use Shopify Section Rendering API
        const sectionId = this.sectionId.replace('shopify-section-', '');
        const response = await fetch(`${this.routes.cartUrl}?section_id=${sectionId}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch cart section');
        }

        const html = await response.text();

        // Parse the HTML and extract the section content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`[data-section-id="${this.sectionId}"]`);

        if (newSection) {
          // Replace the entire wrapper content
          const currentWrapper = this.querySelector(`.${this.classes.wrapperMainCart}`);
          const newWrapper = newSection.querySelector(`.${this.classes.wrapperMainCart}`);

          if (currentWrapper && newWrapper) {
            currentWrapper.innerHTML = newWrapper.innerHTML;
          }
        }
      } catch (error) {
        console.error('Failed to re-render cart:', error);
        // Fallback to page reload if section rendering fails
        window.location.reload();
      }
    }

    /**
     * Broadcast an update start event
     * @param {string} lineKey - The line key of the item that was updated
     * @param {string} action - The action that was performed
     * @returns {void}
     */
    _broadcastUpdateStart(lineKey, action) {
      const event = new CustomEvent('wlsn:cart:update:start', {
        detail: { lineKey, action },
      });
      document.dispatchEvent(event);
    }

    /**
     * Broadcast an update success event
     * @param {string} lineKey - The line key of the item that was updated
     * @param {object} cart - The cart object
     * @param {string} action - The action that was performed
     * @returns {void}
     */
    _broadcastUpdateSuccess(lineKey, cart, action) {
      const updatedItem = cart.items.find((item) => item.key === lineKey);

      const event = new CustomEvent('wlsn:cart:update:success', {
        detail: { lineKey, updatedItem, cart, action },
      });
      document.dispatchEvent(event);
    }

    /**
     * Broadcast an update error event
     * @param {string} lineKey - The line key of the item that caused the error
     * @param {string} error - The error message
     * @returns {void}
     */
    _broadcastUpdateError(lineKey, error) {
      const event = new CustomEvent('wlsn:cart:update:error', {
        detail: { lineKey, error },
      });
      document.dispatchEvent(event);
    }

    /**
     * Set the loading state of the cart
     * @param {boolean} loading - The loading state
     * @returns {void}
     */
    _setLoadingState(loading) {
      this.isLoading = loading;
      this.classList.toggle(this.classes.loadingState, loading);
      this.classList.toggle(this.classes.loading, loading);
    }

    /**
     * Update the sections with the new data
     * @param {object} data - The data to update the sections with
     * @returns {void}
     */
    _updateSections(data) {
      if (!data || !data.sections) return;

      this.sectionsToUpdate.forEach((section) => {
        const sectionHtml = data.sections[section.id];
        if (!sectionHtml) return;

        // If section has custom update method, use it; otherwise use generic update
        if (section.updateMethod && this[section.updateMethod]) {
          this[section.updateMethod](sectionHtml, section);
        } else {
          this._updateSectionGeneric(sectionHtml, section);
        }
      });
    }

    /**
     * Generic update method for sections that don't have a custom update method
     * It will replace the entire section with the new HTML
     * @param {string} sectionHtml - The new HTML for the section
     * @param {object} section - The section object
     * @returns {void}
     */
    _updateSectionGeneric(sectionHtml, section) {
      if (!section.selector) return;

      const elements = document.querySelectorAll(section.selector);

      elements.forEach((element) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const newElement = doc.querySelector(section.selector);

        if (newElement && element.parentElement) {
          element.parentElement.replaceChild(newElement, element);
        }
      });
    }
  }

  customElements.define('wlsn-main-cart', WLSNMainCart);
}
